import { useCallback, useMemo } from "react";
import { useMatches, useSearchParams } from "react-router-dom";
import { useLanguage } from "../components/Language";
import { LS2PacRouteHandle } from "../components/App";
import { useLocationConfiguration } from "../components/LocationConfiguration";

export function usePageTitle() {
  const matches = useMatches();
  const { translate } = useLanguage();
  const { locationConfiguration } = useLocationConfiguration();
  const libraryName = locationConfiguration?.libraryName;
  const [searchParams] = useSearchParams();

  const forbiddenPageTitle = useMemo(() => {
    return `${translate("Forbidden")} | ${libraryName}`;
  }, [libraryName, translate]);

  const pageNotFoundPageTitle = useMemo(() => {
    return `${translate("PageNotFound")} | ${libraryName}`;
  }, [libraryName, translate]);

  const getPageTitle = useCallback(() => {
    const parts: string[] = [];

    // iterate throught route matches in reverse order
    for (let i = matches.length - 1; i >= 0; i--) {
      const { handle, data } = matches[i];
      const { title, titleStrategy } = (handle as LS2PacRouteHandle) || {};

      // if title is not defined, skip
      if (!title) continue;

      const resolvedTitle =
        typeof title === "function"
          ? title(translate, searchParams, data)
          : translate(title);

      // if resolved title is empty, skip (in case we make title required and use null to skip)
      if (!resolvedTitle) continue;

      parts.push(resolvedTitle);

      // if titleStrategy is "replace", stop processing up the route tree
      if (titleStrategy === "replace") {
        break;
      }
    }

    // if we have no route titles, use the library name as the title
    if (parts.length === 0) {
      return libraryName;
    }

    return `${parts.join(" > ")} | ${libraryName}`;
  }, [matches, libraryName, searchParams, translate]);

  return { getPageTitle, forbiddenPageTitle, pageNotFoundPageTitle };
}

export const getSearchResultsTitleFormatter = (databaseNameToken?: string) => {
  return (
    translate: (key: string) => string,
    searchParams: URLSearchParams,
    _: unknown,
  ) => {
    const searchTerm = searchParams.get("term");
    if (searchTerm) {
      return databaseNameToken
        ? `${translate("searchResultsFor")} "${searchTerm}" > ${translate(
            databaseNameToken,
          )}`
        : `${translate("searchResultsFor")} "${searchTerm}"`;
    }

    return databaseNameToken
      ? `${translate("results")} > ${translate(databaseNameToken)}`
      : translate("results");
  };
};
